// Shared strike-label policy for custom-drawn price axes.
//
// The sibling of chartTimeAxis: a fixed strike modulus has the same defect as a
// fixed time cadence, because pixels-per-strike moves with both the visible
// strike range and the panel height. The ladder holds only divisors of 100 so
// labels always land on the round numbers traders anchor to.

export const STRIKE_STEP_LADDER = [1, 5, 10, 25, 50, 100];

export const DEFAULT_STRIKE_LABEL_HEIGHT_PX = 11;
const STRIKE_LABEL_GAP_PX = 7;
const ONE_POINT_LABEL_MAX_SPAN = 10;

function strikeSpan(strikes) {
  if (!Array.isArray(strikes) || strikes.length < 2) return 0;
  let min = Infinity;
  let max = -Infinity;
  for (const strike of strikes) {
    if (!Number.isFinite(strike)) continue;
    if (strike < min) min = strike;
    if (strike > max) max = strike;
  }
  return Number.isFinite(min) && Number.isFinite(max) ? max - min : 0;
}

export function priceAxisLabelValues(range, step = 1) {
  const from = Number(range?.from);
  const to = Number(range?.to);
  const increment = Number(step);
  if (!Number.isFinite(from) || !Number.isFinite(to) || !(to > from) || !(increment > 0)) {
    return [];
  }

  const first = Math.ceil(from / increment) * increment;
  const last = Math.floor(to / increment) * increment;
  const values = [];
  for (let value = first; value <= last; value += increment) {
    values.push(Number(value.toFixed(8)));
  }
  return values;
}

/**
 * Pick the finest ladder step whose label count fits the plot height.
 *
 * Callers that share a tile must call this once and pass the result to every
 * surface, so a widget and the histogram beside it cannot disagree.
 */
export function chooseStrikeLabelStep({
  strikes,
  plotHeightPx,
  labelHeightPx = DEFAULT_STRIKE_LABEL_HEIGHT_PX,
}) {
  const span = strikeSpan(strikes);
  if (span <= 0) return 5;
  if (!Number.isFinite(plotHeightPx) || plotHeightPx <= 0) {
    return STRIKE_STEP_LADDER[STRIKE_STEP_LADDER.length - 1];
  }

  const slotHeight = Math.max(1, labelHeightPx + STRIKE_LABEL_GAP_PX);
  const maxLabels = Math.max(1, Math.floor(plotHeightPx / slotHeight));

  const minimumStep = span <= ONE_POINT_LABEL_MAX_SPAN ? 1 : 5;
  for (const step of STRIKE_STEP_LADDER) {
    if (step < minimumStep) continue;
    if (span / step + 1 <= maxLabels) return step;
  }
  return STRIKE_STEP_LADDER[STRIKE_STEP_LADDER.length - 1];
}

export function isLabeledStrike(strike, step) {
  return Number.isFinite(strike) && step > 0 && Math.round(strike) % step === 0;
}

/**
 * Project the labeled strikes onto the axis, dropping anything outside the plot
 * or colliding with an already-placed label.
 *
 * Coarser strikes win ties: when two labels crowd, the one on the larger round
 * number is the more useful landmark.
 */
export function selectStrikeLabels({
  strikes,
  step,
  toCoordinate,
  plotTop,
  plotBottom,
  labelHeightPx = DEFAULT_STRIKE_LABEL_HEIGHT_PX,
}) {
  if (!Array.isArray(strikes) || !strikes.length || !(step > 0)) return [];
  if (typeof toCoordinate !== 'function') return [];

  const top = Number.isFinite(plotTop) ? plotTop : -Infinity;
  const bottom = Number.isFinite(plotBottom) ? plotBottom : Infinity;
  const halfLabel = labelHeightPx / 2;
  const minGapPx = labelHeightPx + STRIKE_LABEL_GAP_PX;

  const candidates = [];
  for (const strike of strikes) {
    if (!isLabeledStrike(strike, step)) continue;
    const y = toCoordinate(strike);
    if (!Number.isFinite(y) || y < top + halfLabel || y > bottom - halfLabel) continue;
    const rounded = Math.round(strike);
    candidates.push({ strike: rounded, y, rank: coarseness(rounded) });
  }
  if (!candidates.length) return [];

  const ordered = candidates.slice().sort((a, b) => (b.rank - a.rank) || (a.y - b.y));
  const placed = [];
  for (const candidate of ordered) {
    if (placed.some((entry) => Math.abs(entry.y - candidate.y) < minGapPx)) continue;
    placed.push(candidate);
  }

  return placed.sort((a, b) => a.y - b.y).map(({ strike, y }) => ({ strike, y }));
}

function coarseness(strike) {
  for (let index = STRIKE_STEP_LADDER.length - 1; index >= 0; index -= 1) {
    if (strike % STRIKE_STEP_LADDER[index] === 0) return index;
  }
  return -1;
}
